/**
 * Google Analytics Event Tracking Utilities
 * Measurement ID: G-5SKMPJXECJ
 * Includes consent mode checking
 */

// Check if gtag is available
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Check if user has granted analytics consent
const hasAnalyticsConsent = () => {
  const consent = localStorage.getItem('cookie_consent');
  if (!consent) return false;
  
  try {
    const consentData = JSON.parse(consent);
    return consentData.analytics_storage === 'granted';
  } catch (e) {
    return false;
  }
};

/**
 * Track page view
 * @param {string} path - Page path
 */
export const trackPageView = (path) => {
  if (isGtagAvailable() && hasAnalyticsConsent()) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: document.title
    });
    console.log('[Analytics] Page view tracked:', path);
  }
};

/**
 * Track user registration conversion
 * @param {string} userId - User ID
 * @param {string} email - User email
 */
export const trackRegistration = (userId, email) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'sign_up', {
      method: 'Email',
      user_id: userId,
      email: email,
      event_category: 'engagement',
      event_label: 'User Registration'
    });
    
    // Track as conversion
    window.gtag('event', 'conversion', {
      send_to: 'G-5SKMPJXECJ',
      event_category: 'Conversion',
      event_label: 'New User Registration',
      value: 1
    });
    
    console.log('[Analytics] Registration conversion tracked:', email);
  }
};

/**
 * Track service selection/order creation
 * @param {string} serviceName - Name of the service
 * @param {number} price - Service price
 */
export const trackServiceSelection = (serviceName, price) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'begin_checkout', {
      items: [{
        item_name: serviceName,
        price: price,
        currency: 'INR'
      }],
      value: price,
      currency: 'INR',
      event_category: 'ecommerce',
      event_label: serviceName
    });
    
    console.log('[Analytics] Service selection tracked:', serviceName, price);
  }
};

/**
 * Track payment completion (Main conversion goal)
 * @param {string} serviceName - Name of the service
 * @param {number} amount - Payment amount
 * @param {string} transactionId - Transaction ID
 */
export const trackPayment = (serviceName, amount, transactionId) => {
  if (isGtagAvailable()) {
    // Track purchase event
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: amount,
      currency: 'INR',
      items: [{
        item_name: serviceName,
        price: amount,
        quantity: 1
      }],
      event_category: 'ecommerce',
      event_label: 'Payment Completed'
    });
    
    // Track as conversion
    window.gtag('event', 'conversion', {
      send_to: 'G-5SKMPJXECJ',
      value: amount,
      currency: 'INR',
      transaction_id: transactionId,
      event_category: 'Conversion',
      event_label: 'Payment Success'
    });
    
    console.log('[Analytics] Payment conversion tracked:', serviceName, amount);
  }
};

/**
 * Track contact form submission
 * @param {string} name - User name
 * @param {string} email - User email
 */
export const trackContactForm = (name, email) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'generate_lead', {
      event_category: 'engagement',
      event_label: 'Contact Form Submission',
      value: 1
    });
    
    // Track as conversion
    window.gtag('event', 'conversion', {
      send_to: 'G-5SKMPJXECJ',
      event_category: 'Conversion',
      event_label: 'Contact Form Lead',
      value: 1
    });
    
    console.log('[Analytics] Contact form lead tracked:', email);
  }
};

/**
 * Track button click
 * @param {string} buttonName - Name/label of the button
 * @param {string} location - Where the button is located
 */
export const trackButtonClick = (buttonName, location) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'click', {
      event_category: 'engagement',
      event_label: `${location} - ${buttonName}`,
      button_name: buttonName,
      button_location: location
    });
    
    console.log('[Analytics] Button click tracked:', buttonName, location);
  }
};

/**
 * Track document upload
 * @param {string} docType - Type of document
 */
export const trackDocumentUpload = (docType) => {
  if (isGtagAvailable()) {
    window.gtag('event', 'file_upload', {
      event_category: 'engagement',
      event_label: 'Document Upload',
      doc_type: docType
    });
    
    console.log('[Analytics] Document upload tracked:', docType);
  }
};

/**
 * Track custom event
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackCustomEvent = (eventName, params = {}) => {
  if (isGtagAvailable()) {
    window.gtag('event', eventName, params);
    console.log('[Analytics] Custom event tracked:', eventName, params);
  }
};

export default {
  trackPageView,
  trackRegistration,
  trackServiceSelection,
  trackPayment,
  trackContactForm,
  trackButtonClick,
  trackDocumentUpload,
  trackCustomEvent
};
